import ks from "../ReUsableTS/kBrowserShared";
import LNG from "../ReUsableTS/kLNG";
import { kFluentIcons } from "./kFluentIcons";
import { kContextMenu } from "../ReUsableTS/kContextMenu";
import { kEditTableFillIcons } from "./kEditableTableTypes";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableColumn } from "./kEditableColumn";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTableRow } from "./kEditableTableRow";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableMenus {
    public static SetupRowContextMenu(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            Self.RowContextMenu = Self["_NewContextMenu"](Self.RowContextMenu, [
                ...Self["_StandardContextMenuActions"]("kEditableTable.SetupRowContextMenu"),
                Self["_ExportMenuItem"]("kEditableTable.SetupRowContextMenu"),
            ], "kEditableTable.SetupRowContextMenu");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.SetupRowContextMenu");
        }
    }

    // The top-left corner cell owns the whole-grid actions. Right-clicking it
    // pops a menu whose headline item is Export to Excel, making the export far
    // more discoverable than when it only lived at the bottom of the per-row
    // menu. It then keeps the same row actions the corner offered before
    // (Select all mirrors the corner's left-click; Copy / Remove / Edit in form
    // reuse the row-menu callbacks) so no option was lost by adding the export.
    public static SetupCornerContextMenu(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            Self.CornerContextMenu = Self["_NewContextMenu"](Self.CornerContextMenu, [
                Self["_ExportMenuItem"]("kEditableTable.SetupCornerContextMenu"),
                { Text: LNG.SELECTALL, Click: Self.Selection.SelectAll, Icon: () => kFluentIcons.Checklist().ToURLEncoded() },
                ...Self["_StandardContextMenuActions"]("kEditableTable.SetupCornerContextMenu"),
            ], "kEditableTable.SetupCornerContextMenu");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.SetupCornerContextMenu");
        }
    }

    /** Moved from kEditableTable._NewContextMenu (size split); the class member delegates here. */
    public static _NewContextMenu(Self: kEditableTable, Fallback: kContextMenu | null, Items: { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[], Label: string): kContextMenu | null {
        if (ks.KErrorMode === true) { return Fallback; }
        try {
            const Menu: kContextMenu = new kContextMenu();
            Self["_BuildMenuItems"](Menu, Items, Label);
            return Menu;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._NewContextMenu");
            return Fallback;
        }
    }

    /** Moved from kEditableTable._StandardContextMenuActions (size split); the class member delegates here. */
    public static _StandardContextMenuActions(Self: kEditableTable, Label: string): { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[] {
        if (ks.KErrorMode === true) { return []; }
        try {
            return [
                { Text: LNG.EDITINFORM, Click: Self.RightClickEditInForm, Icon: () => kFluentIcons.Edit().ToURLEncoded(), OnlyWhen: () => Self.IsFormEditEnabled() === true },
                { Text: LNG.COPY, Click: Self.CopyClick, Icon: () => kFluentIcons.Copy().ToURLEncoded() },
                // REMOVE IS OFFERED ONLY WHERE IT WOULD WORK. On a read-only or fully-locked grid the
                // action refuses (RemoveRowContextMenuClick), and a menu item that silently does nothing
                // reads as a bug in the grid rather than as a rule — so it is omitted, not disabled.
                // Copy and Export to Excel stay: neither writes.
                { Text: LNG.REMOVE, Click: Self.RemoveRowContextMenuClick, Icon: () => kFluentIcons.Delete().ToURLEncoded(), OnlyWhen: () => Self.IsEffectivelyReadOnly() === false },
            ];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._StandardContextMenuActions");
            return [];
        }
    }

    /** Moved from kEditableTable._ExportMenuItem (size split); the class member delegates here. */
    public static _ExportMenuItem(Self: kEditableTable, Label: string): { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean } {
        if (ks.KErrorMode === true) { return { Text: "", Click: () => { return; } }; }
        try {
            return { Text: LNG.EXPORTTOEXCEL, Click: Self.ExportToExcel, Icon: () => kFluentIcons.Excel().ToURLEncoded() };
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._ExportMenuItem");
            return { Text: "", Click: () => { return; } };
        }
    }

    /** Moved from kEditableTable._BuildMenuItems (size split); the class member delegates here. */
    public static _BuildMenuItems(Self: kEditableTable, Menu: kContextMenu, Items: { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean }[], Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let Item: { Text: string; Click: () => void; Icon?: () => string; OnlyWhen?: () => boolean };
            for (Item of Items) {
                if (Item.OnlyWhen !== undefined && Item.OnlyWhen() === false) { continue; }
                if (Item.Icon !== undefined) {
                    Menu.AddMenuItem(Item.Text, Item.Click, Item.Icon());
                } else {
                    Menu.AddMenuItem(Item.Text, Item.Click);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._BuildMenuItems");
            return ;
        }
    }

    public static SetupColContextMenu(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
           
            Self.ColContextMenu = new kContextMenu();
            Self.ColContextMenu.AddMenuItem("↓  "+ LNG.SORTCOLUMNASC,Self.RightClickSortAscending);
            Self.ColContextMenu.AddMenuItem("↑  " +LNG.SORTCOLUMNDESC,Self.RightClickSortDecending);
            if (Self.ShowColumnFilters === true) {
                Self.ColContextMenu.AddMenuItem("⧩  " + LNG.FILTERTHISCOLUMN, Self.RightClickFilterColumn);
                Self.ColContextMenu.AddMenuItem("✕  " + LNG.FILTERCLEARTHISCOLUMN, Self.RightClickClearColumnFilter);
                Self.ColContextMenu.AddMenuItem("⚙  " + LNG.FILTERADVANCED, Self.OpenAdvancedFilter);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.SetupColContextMenu");
            return ;
        }
    }

    public static RightClickSortAscending(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_RightClickSort"](Self.SortAscending, "kEditableTable.RightClickSortAscending");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RightClickSortAscending");
        }
    }

    public static RightClickSortDecending(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_RightClickSort"](Self.SortDescending, "kEditableTable.RightClickSortDecending");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RightClickSortDecending");
        }
    }

    /** Moved from kEditableTable._RightClickSort (size split); the class member delegates here. */
    public static _RightClickSort(Self: kEditableTable, Sort: (ColumnIndex?: number) => void, Label: string): void {
        if (ks.KErrorMode === true) { return ; }
        try {
           if(Self.RightClickColumn){
                Sort(Self.RightClickColumn.ColumnIndex);
           }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._RightClickSort");
            return ;
        }
    }

    // Tier-2 column-menu action: open the same Tier-1 popup the funnel opens,
    // anchored to the right-clicked header (feature #2 entry point).
    public static RightClickFilterColumn(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.RightClickColumn === null || Self.FilterUI === null) { return; }
            const Column: kEditableColumn | null = Self["_ColumnByIndex"](Self.RightClickColumn.ColumnIndex);
            if (Column === null || Self.RightClickColumn.HeaderCell === null) { return; }
            Self.FilterUI.OpenColumnPopup(Column, Self.RightClickColumn.HeaderCell);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RightClickFilterColumn");
        }
    }

    public static RightClickClearColumnFilter(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.RightClickColumn === null) { return; }
            const Column: kEditableColumn | null = Self["_ColumnByIndex"](Self.RightClickColumn.ColumnIndex);
            if (Column === null) { return; }
            Self.ClearColumnFilter(Column.KObjectPropertyName);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RightClickClearColumnFilter");
        }
    }

    // Opens the Tier-3 cross-column advanced filter dialog (feature #4 builder).
    // Delegates to the registered FilterUI so the base grid never imports the
    // dialog module.
    public static OpenAdvancedFilter(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.FilterUI === null) { return; }
            Self.FilterUI.OpenAdvanced();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.OpenAdvancedFilter");
        }
    }

    public static SetupRangeContextMenu(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            Self.CellsContextMenu = new kContextMenu();
            Self.CellsContextMenu.AddMenuItem(LNG.COPY,Self.CopyClick,kFluentIcons.Copy().ToURLEncoded());
            // ⛔ THE THREE WRITE ITEMS ARE OMITTED ON A READ-ONLY OR FULLY-LOCKED GRID, exactly as
            // Remove is in SetupRowContextMenu and SetupCornerContextMenu.
            //
            // ⚠ THIS WAS THE MENU THE 2026-08-25 READ-ONLY WORK MISSED, and a LIVE E2E found it the
            // next day: right-clicking a CELL opens THIS menu, not the row one, so a read-only grid
            // was still offering Paste / Fill down / Fill right. All three already REFUSED at the
            // action (RightClickPaste and the two Fill methods test ReadOnly and Locked per cell), so
            // nothing could be written — but an item that silently does nothing reads as a bug in the
            // grid, which is the whole reason the other two menus drop Remove rather than no-op it.
            // Three menus, one rule; a guard applied to two of three is the recurring defect here.
            //
            // COPY stays, and the filter item below stays: neither writes.
            if (Self.IsEffectivelyReadOnly() === false) {
                Self.CellsContextMenu.AddMenuItem(LNG.PASTE,Self.RightClickPaste,kFluentIcons.Paste().ToURLEncoded());
                Self.CellsContextMenu.AddMenuItem(LNG.FILLDOWN,Self.FillDown, kEditTableFillIcons.FillDown);
                Self.CellsContextMenu.AddMenuItem(LNG.FILLRIGHT,Self.FillRight,kEditTableFillIcons.FillRight);
            }
            if (Self.ShowColumnFilters === true) {
                Self.CellsContextMenu.AddMenuItem(LNG.FILTERBYTHISVALUE, Self.RightClickFilterByValue);
            }
            if (Self.OnCellsMenuSetup !== null) { Self.OnCellsMenuSetup(Self.CellsContextMenu); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.SetupRangeContextMenu");
            return ;
        }
    }

    public static _UpdateFillMenuVisibility(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!Self.CellsContextMenu) { return; }
            const MultiRow: boolean = Self.Selection.RowIndexEnd > Self.Selection.RowIndexStart;
            const MultiCol: boolean = Self.Selection.ColIndexEnd > Self.Selection.ColindexStart;
            let RowDisplay: string = "none";
            if (MultiRow === true) { RowDisplay = ""; }
            let ColDisplay: string = "none";
            if (MultiCol === true) { ColDisplay = ""; }
            for (const Item of Self.CellsContextMenu.MenuItems.Items) {
                if (Item.Title === LNG.FILLDOWN) {
                    Item.DivItem.style.display = RowDisplay;
                } else if (Item.Title === LNG.FILLRIGHT) {
                    Item.DivItem.style.display = ColDisplay;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._UpdateFillMenuVisibility");
        }
    }

    /** Moved from kEditableTable._FillableCell (size split); the class member delegates here. */
    public static _FillableCell(Self: kEditableTable, Row: number, Col: number, Label: string): kEditableTableCell | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            const CurrTarget: kEditableTableCell = Self.GetCellByIndexes(Row, Col);
            if (CurrTarget.ColDef && CurrTarget.ColDef.Locked === false) {
                return CurrTarget;
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._FillableCell");
            return null;
        }
    }

    /** Moved from kEditableTable._Fill (size split); the class member delegates here. */
    public static _Fill(Self: kEditableTable, Down: boolean, Label: string): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            let RowCounter:number = 0;
            let ColCounter:number = 0;
            let CurrTarget:kEditableTableCell | null = null;
            let CurrSource:kEditableTableCell;
            for(RowCounter=Self.Selection.RowIndexStart;RowCounter<=Self.Selection.RowIndexEnd;RowCounter ++){
                const CurrRow = Self.Rows.Items[RowCounter];
                if (CurrRow && CurrRow.ReadOnly) { continue; }
                for(ColCounter = Self.Selection.ColindexStart;ColCounter<=Self.Selection.ColIndexEnd;ColCounter++){
                    if(Down === true && RowCounter <= Self.Selection.RowIndexStart){ continue; }
                    if(Down !== true && ColCounter <= Self.Selection.ColindexStart){ continue; }
                    CurrTarget = Self["_FillableCell"](RowCounter, ColCounter, Label);
                    if (CurrTarget !== null) {
                        if (Down === true) {
                            CurrSource = Self.GetCellByIndexes(Self.Selection.RowIndexStart,ColCounter);
                        } else {
                            CurrSource = Self.GetCellByIndexes(RowCounter,Self.Selection.ColindexStart);
                        }
                        CurrTarget.SetNewValue(CurrSource.CurrentValue);
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._Fill");
            return ;
        }
    }

    public static FillDown(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_Fill"](true, "kEditableTable.FillDown");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.FillDown");
        }
    }

    public static FillRight(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_Fill"](false, "kEditableTable.FillRight");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.FillRight");
        }
    }

    // Used to be declared deferred while waiting on nothing — ks.CopyToClipboard is
    // synchronous and every caller (Ctrl+C, the three context menus) uses this as a
    // plain void click handler, so it is now plain void.
    public static CopyClick(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            const CurrText:string = Self.Selection.GetSelectedText();
            ks.CopyToClipboard(CurrText);
            ks.ShowNotification(CurrText);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.CopyClick");
            return ;
        }
    }

    // GetClipboardData removed 2026-08-06: it had no caller anywhere in the estate
    // (verified with tests/find-typed-consumers.js over all 518 files that can import
    // this module) and called `navigator.clipboard.getData("Text")`, which is not an
    // API that exists — the real one is `navigator.clipboard.readText()`. Reinstate it
    // as a callback-style reader (never async) if a paste path ever needs it.
    public static RemoveRow(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            // ⛔ REFUSED ON A READ-ONLY OR FULLY-LOCKED GRID (owner, 2026-08-25). Enforced HERE, at the
            // action, not only by hiding the menu item: the menu is one route in, and a guard that
            // lives in the menu is a guard any other caller walks straight past.
            if (Self.IsEffectivelyReadOnly() === true) { return; }
            if(Self.RightClickRow && Self.RightClickRow.Row){
                if (Self.RightClickRow.ReadOnly) { return; }
                Self.RightClickRow.Remove(undefined);
                Self.UpdateRowNumbers();
            }else{
                Self["_RemoveSelectedRows"]();
                Self["_ReindexRows"]();
            }

        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RemoveRow");
            return ;
        }
    }

    /** Moved from kEditableTable._RemoveSelectedRows (size split); the class member delegates here. */
    public static _RemoveSelectedRows(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            // ⛔ REFUSED ON A READ-ONLY OR FULLY-LOCKED GRID — see RemoveRow. This is the multi-row
            // path, reached when no single row was right-clicked, and it needs its own guard: the
            // per-row `ReadOnly` skip below protects rows individually, but a grid the caller declared
            // read-only should refuse the whole operation rather than quietly remove nothing.
            if (Self.IsEffectivelyReadOnly() === true) { return; }
            let CurrRow:kEditableTableRow;
            let CurrCell:kEditableTableCell;
            let RowIndex:number = 0;
            for(CurrRow of Self.Rows.Items){
                if(CurrRow.Row){
                    for(CurrCell of CurrRow.Cells){
                        if(CurrCell.Cell.classList.contains("SelectedCellSingle") || CurrCell.Cell.classList.contains("SelectedCellMulti")){
                            if(Self.RowDeletedCB){
                                Self.RowDeletedCB(CurrRow);
                            }
                            CurrRow.Row.remove();
                            Self.Rows.Items.splice(RowIndex,1);
                            break;
                        }
                    }
                }
                RowIndex++;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._RemoveSelectedRows");
        }
    }

    /** Moved from kEditableTable._ReindexRows (size split); the class member delegates here. */
    public static _ReindexRows(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            let CurrRow:kEditableTableRow;
            let CurrCell:kEditableTableCell;
            let RowIndex:number = 0;
            for(CurrRow of Self.Rows.Items){
                CurrRow.Index = RowIndex;
                if(CurrRow.RowNumberCell){
                    CurrRow.SetRowNumberText((RowIndex +1).toString());
                    for(CurrCell of CurrRow.Cells){
                        CurrCell.RowIndex = RowIndex;
                    }
                }
                RowIndex++;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._ReindexRows");
        }
    }

    public static RemoveRowContextMenuClick(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            // ⛔ REFUSED ON A READ-ONLY OR FULLY-LOCKED GRID — see RemoveRow. SetupRowContextMenu no
            // longer OFFERS this item on such a grid, so reaching here means something else called it;
            // that is exactly when a guard earns its place.
            if (Self.IsEffectivelyReadOnly() === true) { return; }
            let RowIndexCounter:number = 0;
            for(RowIndexCounter = Self.Selection.RowIndexEnd;RowIndexCounter >=Self.Selection.RowIndexStart;RowIndexCounter --){
                const CurrRow:kEditableTableRow = Self.Rows.Items[RowIndexCounter];
                if (CurrRow.ReadOnly) { continue; }
                CurrRow.Remove(undefined);
            }
            if(Self.Rows.Items.length === 0){
                Self.AddEmptyRows(1);
            }
            Self.UpdateRowNumbers();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RemoveRowContextMenuClick");
            return ;
        }
    }

    // Right-click parity for "Edit in form": opens the dialog for the row the
    // context menu was raised on (RightClickRow, set by kEditableTableRow).
    public static RightClickEditInForm(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.RightClickRow !== null) {
                Self.OpenRowForm(Self.RightClickRow);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus.RightClickEditInForm");
        }
    }

    public static _RemoveNextRow(Self: kEditableTable): void {

        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self["_RowsToBeDeleted"]){
                Self["_RowBeingDeleted"] = Self["_RowsToBeDeleted"].Items[Self["_RowCounter"]];
                if(Self["_RowBeingDeleted"]){
                    Self["_RowBeingDeleted"].Remove(Self["_AfterRemoveConfirmation"]);
                }

            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._RemoveNextRow");
            return ;
        }
    }

    public static _AfterRemoveConfirmation(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return ; }
        try {
            if(Self["_RowBeingDeleted"]){
                Self["_RowBeingDeleted"].FadeOut(Self["_RowBeingDeleted"].RemoveAfterFadeOut);
                Self["_RowCounter"]++;
                Self["_RemoveNextRow"]();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableMenus._AfterRemoveConfirmation");
            return ;
        }
    }
}
